import { APIClient } from "../helpers/api_helper";

// Bring-your-own-AI research API.
// The axios interceptor already unwraps `response.data`, so each call resolves
// to the JSON body directly.
const api = new APIClient();

// Suggested assets, models, prompt styles and timeframes the wizard can offer.
// `assets` is a shortlist to click, not the limit — searchResearchSymbols finds
// anything our data providers can serve.
export const getResearchOptions = () => api.get("/api/user-predictions/options");

// Look up any instrument by ticker or name. Resolves to a list of
// { symbol, name, assetClass, exchange, currency }.
export const searchResearchSymbols = (q, limit = 15) =>
  api.get("/api/user-predictions/symbols/search", { q, limit });

// Build a prompt to paste into an external AI. Resolves to
// { promptId, prompt, symbol, timeframe, style, currentPrice, priceSource, priceAsOf }.
// promptId is what identifies this prompt when the answer is submitted.
export const buildResearchPrompt = ({ symbol, timeframe, style, currentPrice }) =>
  api.create("/api/user-predictions/prompt", { symbol, timeframe, style, currentPrice });

// Submit the model's answer against the prompt the server issued.
// Payload: { promptId, responseText, isPublic }. Price, symbol and timestamp
// are taken from the stored prompt, not sent from here -- and which model
// this is comes from the answer's own self-report, not a value picked here
// either; the server rejects a prompt that already has that model's answer,
// or the same text saved twice, rather than the client pre-checking either.
// Resolves to either a saved row, or { needsConfirmation } when the answer
// collides with a model already recorded against this prompt -- the sign that a
// model misreported itself, which in practice means DeepSeek. Nothing is saved
// in that case; re-send with confirmModel: "deepseek" to file it as DeepSeek
// under whatever version the member's last DeepSeek answer used.
export const submitResearchResponse = ({ promptId, responseText, isPublic, confirmModel }) =>
  api.create("/api/user-predictions/predictions", { promptId, responseText, isPublic, confirmModel });

// scope: "mine" | "shared" | "all". symbols/timeframes/models take
// comma-separated lists: each ORs within itself and ANDs across the others.
export const listResearch = (params = {}) => {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== "")).toString();
  return api.get(`/api/user-predictions/predictions${query ? `?${query}` : ""}`);
};

// Scored performance split by prompt style: range coverage, calibration buckets,
// direction accuracy and data-access mix, per style and per model within it.
// scope: "mine" | "shared" | "all".
export const getResearchScorecard = (params = {}) => {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== "")).toString();
  return api.get(`/api/user-predictions/scorecard${query ? `?${query}` : ""}`);
};

// The symbols, horizons and models present in a scope, with row counts.
export const getResearchFacets = (params) =>
  api.get("/api/user-predictions/facets", params);

// Delete one of your own research rows — for a mis-selected model or a wrong
// paste. Refused once the forecast has been scored: at that point it is part of
// the track record.
export const deleteResearch = (id) =>
  api.delete(`/api/user-predictions/predictions/${id}`);

// Share a prediction publicly, or take it private again.
// scope 'group' changes every answer to the same question, which is what the
// detail page offers: the comparison table is the unit a reader sees, so half
// of one published is a table with rows missing.
export const setResearchVisibility = (id, isPublic, scope = "one") =>
  api.update(`/api/user-predictions/predictions/${id}/visibility`, { isPublic, scope });

// Settle a closed forecast by hand: you enter the closing price yourself.
// Nothing here fetches market data, so the record costs nothing to keep.
// Payload: { actualPrice, rationaleStars, tags, note }. Allowed for the owner
// or an admin, once the window has closed and only once.
export const settleResearch = (id, payload) =>
  api.create(`/api/user-predictions/predictions/${id}/settle`, payload);

// Re-publish this question's Blogger post with the current data. Admin-only;
// the server checks again and refuses anyone else. Does nothing for a
// question that has never been published -- that happens on its own once it
// settles, see bloggerSyncService -- this only refreshes one that already has.
export const updatePredictionBlogger = (id) =>
  api.create(`/api/user-predictions/predictions/${id}/blogger-update`, {});

/**
 * Research members have published, without an account.
 *
 * A separate pair of endpoints rather than listResearch with scope="shared":
 * that one requires a token. The server pins the scope, so these cannot be
 * widened by what is passed here.
 */
export const listPublicResearch = (params = {}) => {
  // The query string is built here rather than handed to APIClient.get, which
  // concatenates key + "=" + value with no encoding. Author names contain
  // spaces — "About Stock" — and would go out raw; a name with an ampersand
  // would silently split into two parameters.
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();
  return api.get(`/api/user-predictions/public${qs ? `?${qs}` : ""}`);
};

export const getPublicResearchFacets = () =>
  api.get("/api/user-predictions/public/facets");
